import { useCallback, useContext } from 'react';

import type { QuestionnaireRequirements } from 'clients/Communication/Questionnaire.dto';
import type { Candidate } from 'model';

import type { Vacancy } from '@/clients/AutoCompleteClient/types';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { getSchedulerType } from '@/features/nylas-calendar-manage-connection-modal';
import { getNylasInterviewType } from '@/features/nylas-calendar-manage-connection-modal/lib';
import { useContactCandidateMutation } from 'hooks/shared/useContactCandidateMutation/useContactCandidateMutation';
import { EventBus } from 'services/event-bus/EventBus';
import { useIndeedCommAgent } from 'shared/legacy/contexts/IndeedCommAgentContext';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';

const getIndeedJob = (selectedVacancy: Vacancy) => {
  return selectedVacancy.type === 'INDEED' ? selectedVacancy.originalIndeedJob ?? null : null;
};

const trackIndeedCommAgentFailure = (
  reason: 'missing_attempt_id' | 'missing_notification_id',
  attributes: Record<string, string | number | boolean>,
) => {
  try {
    newrelic.addPageAction('IndeedCommAgentFailure', {
      reason,
      flow: 'indeed_comm_agent',
      timestamp: new Date().toISOString(),
      ...attributes,
    });
  } catch {
    /* empty */
  }
};

type CreateIndeedAttemptParams = {
  candidate: Candidate;
  selectedVacancy: Vacancy;
};

type SendIndeedOutreachParams = {
  candidate: Candidate;
  selectedVacancy: Vacancy;
  subject: string;
  message: string;
  attemptId?: number;
  questionnaire?: QuestionnaireRequirements;
  isAutoSendInterviewInviteEnabled?: boolean;
  improvedJobPreview?: string;
};

export const useIndeedCommFlow = () => {
  const { attemptId: contextAttemptId, setAttemptId, indeedProject, setIndeedProject } = useIndeedCommAgent();
  const contactCandidateMutation = useContactCandidateMutation();
  const { dictionary } = useContext(LocalizationContext);
  const { createSnackbar } = useContext(SnackbarContext);
  const { close: closeOutreachModal } = useGlobalDialog(GlobalDialogType.CandidateOutreach);

  const createAttempt = useCallback(
    async ({ candidate, selectedVacancy }: CreateIndeedAttemptParams) => {
      const improvedJobId = selectedVacancy.db_id ? Number(selectedVacancy.db_id) : undefined;
      const attemptResponse = await campaignsClient.createRjIndeedOutreachAttempt({
        campaign_id: candidate.campaignId,
        es_person_id: candidate.es_person_id,
        improved_job_id: improvedJobId,
        metadata: indeedProject
          ? { indeed_project_key: indeedProject.key, indeed_project_name: indeedProject.name }
          : undefined,
      });

      setAttemptId(attemptResponse.data.attempt_id);
      return attemptResponse.data.attempt_id;
    },
    [indeedProject, setAttemptId],
  );

  const sendIndeedOutreach = useCallback(
    async ({
      candidate,
      selectedVacancy,
      subject,
      message,
      attemptId,
      questionnaire,
      isAutoSendInterviewInviteEnabled,
      improvedJobPreview,
    }: SendIndeedOutreachParams) => {
      const resolvedAttemptId = attemptId ?? contextAttemptId;

      if (!resolvedAttemptId) {
        trackIndeedCommAgentFailure('missing_attempt_id', {
          campaignId: candidate.campaignId,
          candidateId: candidate.es_person_id,
          candidateStatus: candidate.status,
          selectedVacancyId: selectedVacancy.db_id ?? '',
          selectedVacancyType: selectedVacancy.type ?? '',
          indeedProjectKey: indeedProject?.key ?? '',
          hasIndeedProject: String(Boolean(indeedProject)),
          hasIndeedJob: String(Boolean(getIndeedJob(selectedVacancy))),
        });
        EventBus.clearTransaction(candidate.es_person_id);
        console.error('handleSendRequest: Missing attemptId for indeed flow');
        closeOutreachModal();
        createSnackbar(dictionary.somethingWentWrong, { variant: 'danger' });
        return false;
      }

      EventBus.startTransaction(candidate.es_person_id, {
        name: candidate.firstName,
        esPersonId: candidate.es_person_id,
        status: candidate.status,
        labels: candidate.reasons,
      });

      try {
        const contactResult = await contactCandidateMutation.mutateAsync({
          sendMethod: 'indeed',
          campaignId: candidate.campaignId,
          candidateStatus: candidate.status,
          activeJobBoard: candidate.activeJobBoard,
          data: {
            esPersonId: candidate.es_person_id,
            subject,
            message,
            indeedProjectId: indeedProject?.key,
            indeedJob: getIndeedJob(selectedVacancy),
            improvedJobPreview,
            job_improved_id: selectedVacancy.db_id ? Number(selectedVacancy.db_id) : undefined,
            isReply: false,
            metadata: null,
            candidateName: candidate.name,
            isAiLeveraged: true,
          },
        });

        if (!contactResult?.candidate_notification_id) {
          EventBus.clearTransaction(candidate.es_person_id);
          trackIndeedCommAgentFailure('missing_notification_id', {
            campaignId: candidate.campaignId,
            candidateId: candidate.es_person_id,
            candidateStatus: candidate.status,
            selectedVacancyId: selectedVacancy.db_id ?? '',
            selectedVacancyType: selectedVacancy.type ?? '',
            indeedProjectKey: indeedProject?.key ?? '',
            hasIndeedProject: String(Boolean(indeedProject)),
            hasIndeedJob: String(Boolean(getIndeedJob(selectedVacancy))),
            attemptId: resolvedAttemptId,
            contactResultType: typeof contactResult,
            hasContactResult: String(Boolean(contactResult)),
            contactResult: JSON.stringify(contactResult ?? {}),
          });
          closeOutreachModal();
          console.error('handleSendRequest: Missing candidate_notification_id from contactCandidateMutation response');
          createSnackbar(dictionary.somethingWentWrong, { variant: 'danger' });
          return false;
        }

        await campaignsClient.confirmRjIndeedOutreachAttempt({
          campaign_id: candidate.campaignId,
          es_person_id: candidate.es_person_id,
          attempt_id: resolvedAttemptId,
          candidate_notification_id: contactResult.candidate_notification_id,
          questionnaire,
          schedule_interview: isAutoSendInterviewInviteEnabled,
          scheduler_type: getSchedulerType(getNylasInterviewType()),
        });

        EventBus.commitTransaction(candidate.es_person_id);

        return true;
      } catch (error) {
        EventBus.clearTransaction(candidate.es_person_id);
        throw error;
      }
    },
    [contextAttemptId, closeOutreachModal, contactCandidateMutation, createSnackbar, dictionary, indeedProject],
  );

  return {
    createAttempt,
    sendIndeedOutreach,
    selectedIndeedProject: indeedProject,
    setSelectedIndeedProject: setIndeedProject,
  };
};
